export type LaneHistoryDefaults = {
  originRadiusMiles: number;
  destinationRadiusMiles: number;
  sinceDaysAgo: number;
};

export const LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS = [
  3, 7, 30, 60, 90, 180, 360,
];

export const DEFAULT_LANE_HISTORY_DEFAULTS: LaneHistoryDefaults = {
  originRadiusMiles: 50,
  destinationRadiusMiles: 50,
  sinceDaysAgo: LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS[0],
};

const LANE_HISTORY_DEFAULTS_LOCAL_STORAGE_KEY = 'lane-history-defaults';

export const getLaneHistoryDefaultsLocalStorageKey = (organizationId: string) =>
  `${LANE_HISTORY_DEFAULTS_LOCAL_STORAGE_KEY}:${organizationId}`;

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const canUseLocalStorage = () => typeof window !== 'undefined';

export const getNextLaneHistoryLookbackPeriod = (
  sinceDaysAgo: number
): number => {
  return (
    LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS.find((days) => days > sinceDaysAgo) ??
    LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS[
      LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS.length - 1
    ]
  );
};

export const getPreviousLaneHistoryLookbackPeriod = (
  sinceDaysAgo: number
): number => {
  for (let i = LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS.length - 1; i >= 0; i--) {
    const days = LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS[i];
    if (days < sinceDaysAgo) {
      return days;
    }
  }

  return LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS[0];
};

export const getBuiltInLaneHistoryDefaults = (): LaneHistoryDefaults => ({
  ...DEFAULT_LANE_HISTORY_DEFAULTS,
});

export const sanitizeLaneHistoryDefaults = (
  defaults?: Partial<LaneHistoryDefaults> | null
): LaneHistoryDefaults => {
  return {
    originRadiusMiles: isPositiveNumber(defaults?.originRadiusMiles)
      ? defaults.originRadiusMiles
      : DEFAULT_LANE_HISTORY_DEFAULTS.originRadiusMiles,
    destinationRadiusMiles: isPositiveNumber(defaults?.destinationRadiusMiles)
      ? defaults.destinationRadiusMiles
      : DEFAULT_LANE_HISTORY_DEFAULTS.destinationRadiusMiles,
    sinceDaysAgo: isPositiveNumber(defaults?.sinceDaysAgo)
      ? defaults.sinceDaysAgo
      : DEFAULT_LANE_HISTORY_DEFAULTS.sinceDaysAgo,
  };
};

export const getLaneHistoryDefaults = (
  organizationId: string
): LaneHistoryDefaults => {
  if (!canUseLocalStorage()) {
    return getBuiltInLaneHistoryDefaults();
  }

  let value: string | null;
  try {
    value = window.localStorage.getItem(
      getLaneHistoryDefaultsLocalStorageKey(organizationId)
    );
  } catch {
    return getBuiltInLaneHistoryDefaults();
  }

  if (!value) {
    return getBuiltInLaneHistoryDefaults();
  }

  try {
    return sanitizeLaneHistoryDefaults(JSON.parse(value));
  } catch {
    return getBuiltInLaneHistoryDefaults();
  }
};

export const setLaneHistoryDefaults = (
  organizationId: string,
  defaults: LaneHistoryDefaults
) => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(
      getLaneHistoryDefaultsLocalStorageKey(organizationId),
      JSON.stringify(sanitizeLaneHistoryDefaults(defaults))
    );
  } catch {
    return;
  }
};
